import { ChangeDetectorRef, ElementRef } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';

const ARROW_NAVIGATION_KEYS = ['ArrowDown', 'ArrowUp', 'Escape', 'Tab', 'Enter'];
type ArrowKeysTuple = typeof ARROW_NAVIGATION_KEYS;
type ArrowKey = ArrowKeysTuple[number];

function isArrowKey(value: string): value is ArrowKey {
    return ARROW_NAVIGATION_KEYS.includes(value as ArrowKey);
}

export class ArrowNavigationHelper {
    private readonly HOVER_DELAY: number = 500;
    private processCounter: number = 0; // Used to prevent multiple calls to setTimeout in a row
    public focusIndex: number = -1;
    private usedKey: boolean = false;
    private lastHovered: HTMLElement | null = null;
    private lastNavigationMethodWasKeyboard: boolean = false;

    constructor(
        private readonly cdr: ChangeDetectorRef,
        private readonly isOpen$: BehaviorSubject<boolean>,
        private readonly destroy$: Subject<void>,
        private readonly OPTION_QUERY: string,
        private readonly stopPropagation: boolean = true
    ) {}

    /**
     * Handle keydown events for dropdown navigation
     */
    public handleKeyDown(config: {
        event: KeyboardEvent;
        optionContainerElement: ElementRef<HTMLElement> | undefined | null;
        toggleContainer: ElementRef<HTMLElement> | undefined | null;
        searchElement?: ElementRef<HTMLElement> | null;
        closeDropdown: () => void;
    }): void {
        // Prevent over-eager catching of events
        if (!isArrowKey(config.event.key)) {
            return;
        }

        // To prevent interfering with other components
        if (this.stopPropagation) {
            config.event.stopPropagation();
        }

        if (!this.isOpen$.value) return; // Don't handle keys if dropdown closed

        if (config.optionContainerElement) {
            if (config.event.key === 'ArrowDown') {
                const maxItems: number = this.onArrowKeyPressed(config.event, config.optionContainerElement);
                this.focusIndex = Math.min(this.focusIndex + 1, maxItems - 1);

                this.lastNavigationMethodWasKeyboard = true;
                this.cdr.markForCheck();
                return;
            }

            if (config.event.key === 'ArrowUp') {
                this.onArrowKeyPressed(config.event, config.optionContainerElement);
                this.focusIndex = Math.max(this.focusIndex - 1, config.searchElement ? -1 : 0);

                this.lastNavigationMethodWasKeyboard = true;
                this.cdr.markForCheck();
                return;
            }
        }

        if (config.event.key === 'Escape') {
            config.closeDropdown();
            (config.toggleContainer?.nativeElement.querySelector('button,a,input') as HTMLElement)?.focus();
        }

        if (config.event.key === 'Tab') {
            config.closeDropdown();
        }
    }

    /**
     * Configures side effects when arrow keys are pressed.
     * - Adds -sh-unhoverable class to current item
     * - Sets focus index to the appropriate item
     * - Prevents the dropdown from closing when arrow keys are pressed
     * @param event
     * @param optionContainerElement
     * @returns The number of items in the dropdown. Used to determine the max focus index.
     */
    private onArrowKeyPressed(event: KeyboardEvent, optionContainerElement: ElementRef<HTMLElement>): number {
        event.preventDefault();

        this.usedKey = true;
        this.processCounter += 1;
        const process: number = this.processCounter;

        if (this.lastHovered) {
            this.lastHovered.classList.add('-sh-unhoverable');
        }

        // Prevents hover from focusing an element within HOVER_DELAY ms of a key event
        // Timer is reset on every event (enforced by the process counter)
        setTimeout(() => {
            if (process === this.processCounter) {
                this.usedKey = false;
                this.processCounter = 0;
            }
        }, this.HOVER_DELAY);

        return this.getChildCount(optionContainerElement.nativeElement);
    }

    /**
     * Focus the appropriate element based on the current focus index
     */
    public manageFocus(
        isOpen: boolean,
        optionContainerElement: ElementRef<HTMLElement> | undefined | null,
        searchElement: ElementRef<HTMLElement> | undefined | null
    ): void {
        if (!isOpen) return; // Don't refocus if dropdown closed

        if (this.focusIndex === -1) {
            if (searchElement) {
                searchElement.nativeElement.focus({ preventScroll: true });
            }
            return;
        }

        if (optionContainerElement) {
            const items: NodeListOf<HTMLElement> = optionContainerElement.nativeElement.querySelectorAll(
                this.OPTION_QUERY
            );
            let item: HTMLElement = items.item(this.focusIndex);
            if (item) {
                if (item.tagName.toLowerCase() === 'sh-action-item') {
                    const tempItem: HTMLElement | null = item.querySelector('a,button');
                    if (!tempItem) {
                        return;
                    }
                    item = tempItem;
                }
                item.focus({ preventScroll: true });
                if (this.lastNavigationMethodWasKeyboard) {
                    item.scrollIntoView({ block: 'nearest', inline: 'nearest' });
                }
            }
        }
    }

    /**
     * Update focus index when hovering over an option
     * Combine with ShChildIndexDirective for best performance.
     * @param hoverElement
     * @param optionContainerElement
     */
    public onHover(
        hoverElement: HTMLElement,
        optionContainerElement: ElementRef<HTMLElement> | null | undefined
    ): void {
        // Set this to false even if hovering over disabled element to prevent jumping when scrolling
        this.lastNavigationMethodWasKeyboard = false;

        // Skip if element has does not match the option query
        if (!hoverElement.matches(this.OPTION_QUERY)) {
            return;
        }

        if (optionContainerElement) {
            // Disable hover functionality if the user rececntly navigated with the keyboard
            if (this.usedKey) {
                hoverElement.classList.add('-sh-unhoverable');
                return;
            }

            // Remove -sh-unhoverable class from current item

            const hoverIndex: number = this.getIndexOfElement(hoverElement, optionContainerElement.nativeElement);
            if (hoverIndex === -1) {
                return;
            }
            hoverElement.classList.remove('-sh-unhoverable');
            this.lastHovered = hoverElement;
            this.focusIndex = hoverIndex;
            this.cdr.markForCheck();
        }
    }

    /**
     * Reset focus when dropdown opens
     */
    public resetFocus(resetIndex: number = -1): void {
        this.focusIndex = resetIndex;
    }

    /**
     * Update focus when a search field is focused
     */
    public onSearchFocus(): void {
        this.focusIndex = -1;
    }

    /**
     * Set the focus index manually.
     * Don't combine this with the handleKeyDown method.
     * @param index
     */
    public setSearchFocusIndex(index: number): void {
        this.focusIndex = index;
    }

    // # startRegion: Util
    private getIndexOfElement(element: HTMLElement, optionContainerElement: HTMLElement): number {
        if (element.dataset.index) {
            // Set using ShChildIndexDirective
            return Number(element.dataset.index);
        }
        return Array.from(optionContainerElement.querySelectorAll(this.OPTION_QUERY)).indexOf(element);
    }

    private getChildCount(optionsContainer: HTMLElement): number {
        if (optionsContainer.dataset.childCount) {
            return Number(optionsContainer.dataset.childCount);
        }

        return optionsContainer.querySelectorAll(this.OPTION_QUERY).length;
    }
    // # endRegion: Private methods
}
